/**
 * Keeping a tab that is left open current.
 *
 * Syncing once at render makes a live client indistinguishable from a frozen
 * one: mail arrives on the relay and the page sits there holding yesterday's.
 * This runs the sync again while the tab is open, and again the moment a
 * backgrounded tab is looked at, so coming back to it is instant rather than up
 * to a minute stale.
 *
 * Focus counts as well as visibility: a window left visible behind another, or
 * on a second monitor, never fires `visibilitychange`, so coming back to it
 * would otherwise wait for the beat.
 *
 * Three rules, all of them about cost rather than tidiness:
 *
 * - **A hidden tab syncs nothing.** With a phone signer every sync is signer
 *   round-trips, and nobody is reading the answer.
 * - **A sync that is still running is never started again.** `sync` is not
 *   re-entrant: two in flight both write `lastSync`, and the one that finishes
 *   last wins whatever it happened to see. Asks that arrive meanwhile become
 *   one more run straight after, so news that lands mid-sync is not left for
 *   the beat.
 * - **A failing sync waits longer each time.** An offline tab that retried on
 *   the flat beat spent a signer round-trip a minute on a server it could not
 *   reach. The delay doubles per failure up to a ceiling, and the first success
 *   puts it back on the beat.
 */

/** Often enough that mail feels live, rare enough to be free. */
export const SYNC_INTERVAL_MS = 60_000;

/** The longest a run of failures can stretch the beat to. */
export const MAX_SYNC_INTERVAL_MS = 30 * 60_000;

/** What `pollWhileVisible` needs of a document, so a test can be one. */
export interface VisibilityHost {
  readonly visibilityState: DocumentVisibilityState;
  addEventListener(type: "visibilitychange", listener: () => void): void;
  removeEventListener(type: "visibilitychange", listener: () => void): void;
}

/** Where focus is heard. The window, or a test's stand-in. */
export interface FocusHost {
  addEventListener(type: "focus" | "pageshow", listener: () => void): void;
  removeEventListener(type: "focus" | "pageshow", listener: () => void): void;
}

export interface Poll {
  /** Run now, unless the tab is hidden or a run is already in flight. */
  now(): void;
  /** Release the timer and the listener. */
  stop(): void;
}

export function pollWhileVisible(
  task: () => Promise<void>,
  host: VisibilityHost = document,
  intervalMs: number = SYNC_INTERVAL_MS,
  focus: FocusHost = window,
): Poll {
  let running = false;
  let again = false;
  let delayMs = intervalMs;
  let timer: ReturnType<typeof setTimeout> | null = null;

  // A fixed interval cannot change its delay, so the beat is a timeout chain
  // and every arm of it goes through here.
  const schedule = (delay: number): void => {
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(run, delay);
  };

  const run = (): void => {
    if (host.visibilityState !== "visible") return;
    if (running) {
      again = true;
      return;
    }
    running = true;
    again = false;
    // The task reports its own failures; what matters here is that a failed run
    // releases the guard, so one bad network moment does not stop the clock —
    // and that it lengthens the beat instead of holding it.
    void task()
      .then(() => {
        delayMs = intervalMs;
      })
      .catch(() => {
        delayMs = Math.min(delayMs * 2, MAX_SYNC_INTERVAL_MS);
      })
      .finally(() => {
        running = false;
        schedule(again ? 0 : delayMs);
      });
  };

  host.addEventListener("visibilitychange", run);
  focus.addEventListener("focus", run);
  focus.addEventListener("pageshow", run);
  schedule(delayMs);

  return {
    now: run,
    stop(): void {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      host.removeEventListener("visibilitychange", run);
      focus.removeEventListener("focus", run);
      focus.removeEventListener("pageshow", run);
    },
  };
}
